import {
  Button,
  DesignLanguage,
  Grid,
  InputAddressData,
  InputBirthdayValidity,
  Text,
  useDesignLanguagePerClass,
  useIsDesignLanguage,
  usePhoneNumber,
} from '@afterpaytouch/core'
import { unwrapResult } from '@reduxjs/toolkit'
import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from '../../utils/convergence'
import { Country, ErrorCode, Cohort } from '@afterpaytouch/portal-api/types'
import { DisabledModal } from '../DisabledModal'
import { FormWrapper } from '../FormWrapper'
import { Trans } from '../../utils/convergence'
import { Route, usePostLoginRoute, useCashAuthRoute } from '../../config/router'
import styles from './style.module.css'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import {
  CheckoutError,
  CreateAccountRequest,
  SupportedLocale,
  VerificationMethod,
  RegistrationErrorCode,
  RegistrationStatus,
  duplicateErrorCodeList,
} from '../../model'
import { TerminalErrorCode, getCheckoutIdentityError } from '../../model/checkout'
import { getEnteredPrefillEvent, TrackingEvent } from '../../model/monitoring'
import {
  accountLookup,
  checkCrossBorder,
  createRegistration,
  finaliseAccountDetails,
  handleEmailLookup,
  setCheckoutError,
  setIsMobileLogin,
  updateConsumerMobile,
  updateRegistrationData,
  updateUseExistingMobile,
  useAppDispatch,
  useAppRouter,
  useAuthentication,
  useCheckoutConsumerNames,
  useCheckoutConsumerPhoneNumber,
  useCheckoutRegistrationAddress,
  useConsumerSelector,
  useCountry,
  useEnableEditName,
  useIsExpress,
  useMobileLogin,
  useRegistration,
  useSupportedCountries,
  useCohort,
} from '../../state'
import { isValidObject } from '@afterpaytouch/form/lib/utils/object'
import { useXbtBrowserRedirect } from '../../utils/redirects'
import { useCheckoutSizes } from '../../utils/responsive'
import { camelCase, isUndefinedOrEmpty, valueIsNotNullOrUndefined } from '@afterpay/utils'
import { CreateAccountAnalyticsFields, EmailInputError, FieldAnalyticsLabels } from './constants'
import { CHECKOUT_FLOW_TYPE, postCheckoutFlowMessage } from '../../integrations/inappbrowser-messenger'
import { Disclaimer } from '../Disclaimer/Disclaimer'
import FormBuilder, { DateOfBirthFieldSchema } from '@afterpaytouch/form'
import { getSchema } from './utils'
import { CreateAccountForm } from './types'
import { compareObjects } from '../../utils/comparison'
import { CreateAccountProfileCard } from './CreateAccountProfileCard'
import { MobileTerms } from './MobileTerms'
import { setDataLayer } from '../../integrations/googletagmanager/utils'
import { GTM_EVENTS_NAME } from '../../integrations/googletagmanager/constant'
import { handleAccountDetailsErrors } from './helper'
import { getDOBValidations } from './validation'

interface PrefilledAmplitudePayload {
  [x: string]: boolean
}

interface TrackingInputPayload {
  nonTerminalError?: string
  mobileNumberLength?: number
  countryCode?: Country
}

type FormErrorType = 'underAge' | 'overAge' | 'invalid' | 'requiredAddressFields' | string

interface FormErrorDetail {
  type?: FormErrorType
  ref?: { value?: string }
}

type FormErrors = Partial<Record<string, FormErrorDetail>>

const DOB_ERROR_MAP = {
  [InputBirthdayValidity.OverAge]: TrackingEvent.DOB_OVER_ERROR,
  [InputBirthdayValidity.UnderAge]: TrackingEvent.DOB_UNDER_ERROR,
}

const LinkButton = ({ onClick, ...otherProps }: React.AnchorHTMLAttributes<HTMLButtonElement>): JSX.Element => (
  <button
    {...otherProps}
    type='button'
    onClick={(e) => {
      e.preventDefault()
      if (onClick !== undefined) {
        onClick(e)
      }
    }}
  />
)

export const getRequest = (
  data: CreateAccountForm,
  initialFormData: CreateAccountForm,
  preferredLocale: SupportedLocale,
  creditCheckAgree: boolean
): CreateAccountRequest => {
  const { givenNames, otherGivenNames, surname, dateOfBirth, mobile, email, address, marketingOptIn } = data
  return {
    ...address,
    countryCode: address.countryCode as Country,
    dateOfBirth,
    givenNames: givenNames ?? initialFormData.givenNames,
    mobile,
    email: email ?? initialFormData.email,
    otherGivenNames: otherGivenNames ?? initialFormData.otherGivenNames,
    surname: surname ?? initialFormData.surname,
    preferredLocale,
    creditCheckAgree,
    marketingOptIn,
  }
}

export const CreateAccount: FunctionComponent = (): JSX.Element => {
  const dispatch = useAppDispatch()
  const auth = useAuthentication()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { t } = useTranslation()
  const { givenNames, surname } = useCheckoutConsumerNames()
  const checkoutConsumerPhoneNumber = useCheckoutConsumerPhoneNumber()
  const supportedCountries = useSupportedCountries()
  const { data: registrationData, status: registrationStatus, error: registrationError, isManualAddress } = useRegistration()
  const country = useCountry()
  const { locale, push } = useAppRouter()
  const postLoginRoute = usePostLoginRoute()
  const { buttonSize } = useCheckoutSizes()
  const { parseMobile } = usePhoneNumber({ country })
  const consumer = useConsumerSelector()
  const isExpress = useIsExpress()
  const isDuplicatePhoneNumberError =
    registrationStatus === RegistrationStatus.Error && registrationError?.error !== undefined && duplicateErrorCodeList.includes(registrationError?.error)
  const isCashRegistrationError = registrationError?.error === RegistrationErrorCode.ExistingPhoneNumberUnicorn

  const [enableEditName, setEnableEditName] = useState(useEnableEditName())
  const [isSubmitted, setIsSubmitted] = useState(false)
  const formDataRef = useRef<CreateAccountForm | null>(null)

  const cashDuplicateErrorKey = `sign-up-${isDuplicatePhoneNumberError ? 'mobile' : 'email'}-cash-error-message-link`
  const initialAddresses = useCheckoutRegistrationAddress()
  const xbtBrowserRedirect = useXbtBrowserRedirect()
  const { isMobileLogin } = useMobileLogin()
  const cohort = useCohort()
  const cashAuthRoute = useCashAuthRoute()
  const shouldVerify = auth.verificationRequired || cohort === Cohort.CashOnly

  const defaultMobile = registrationData?.mobile ?? parseMobile(consumer.profile?.phoneNumber ?? checkoutConsumerPhoneNumber) ?? ''
  const [trackedInputErrors, setTrackedInputErrors] = useState<{ [x: string]: boolean }>({})

  const [isAddressPrefilled, setIsAddressPrefilled] = useState<boolean | null>(null)
  const [oneOffEvents, setOneOffEvents] = useState({
    notLoggedPrefillStatus: true,
    notToggledManualAddress: true,
  })
  const [crossRegionUrl, setCrossRegionUrl] = useState<string>('')
  const [emailInputError, setEmailInputError] = useState<EmailInputError | undefined>(undefined)
  const initialDobValidations = getDOBValidations(t, initialAddresses as InputAddressData, country)
  const [dobValidations, setDobValidations] = useState<Omit<DateOfBirthFieldSchema, 'type'>>(initialDobValidations)
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const designLanguageStyles = useDesignLanguagePerClass(styles)

  const headingContext = shouldVerify ? {} : { context: 'verifiedMobile' }
  const signUpSubtext = t('signUp:description', { ...headingContext })
  const [hasSentAnalyticsData, setHasSentAnalyticsData] = useState({
    givenNames: false,
    otherGivenNames: false,
    surname: false,
    email: false,
    mobile: false,
    dateOfBirth: false,
    address: false,
  })

  const handleCountryChange = useCallback(
    async (countryCode: Country): Promise<void> => {
      logEvent(TrackingEvent.PRESSED_CHANGE_COUNTRY_BUTTON_SIGNUP_PAGE, { countryCode })
      const crossRegionCheck = await dispatch(checkCrossBorder(countryCode, consumer.email, formDataRef.current?.mobile ?? ''))
      if (crossRegionCheck !== null && 'crossRegionCheckoutUrl' in crossRegionCheck) {
        logEvent(TrackingEvent.REDIRECT_CROSS_REGION)
        xbtBrowserRedirect(crossRegionCheck.crossRegionCheckoutUrl, true)
      }
    },
    [consumer.email, dispatch, formDataRef, logEvent, xbtBrowserRedirect]
  )

  const handleInputErrorAnalytics = (trackingEvent: string, payload: TrackingInputPayload = {}): void => {
    const hasPayload = Object.keys(payload).length > 0
    const { nonTerminalError } = payload
    const trackingItem = hasPayload ? nonTerminalError : trackingEvent

    if (trackingItem !== undefined && !trackedInputErrors[trackingItem]) {
      if (hasPayload) {
        logEvent(trackingEvent, payload)
      } else {
        logEvent(trackingEvent)
      }

      setTrackedInputErrors((trackedErrors) => {
        if (hasPayload && typeof nonTerminalError === 'string') {
          return { ...trackedErrors, [nonTerminalError]: true }
        }

        return { ...trackedErrors, [trackingEvent]: true }
      })
    }
  }

  const errorMessageComponentsRedirectToMobileLogin = useMemo(
    () => ({
      login: (
        <LinkButton
          key='sign-up-email-error-message-link'
          data-testid='sign-up-email-error-login-link'
          className={designLanguageStyles.errorLinkButton}
          onClick={() => {
            dispatch(updateUseExistingMobile(true))
            push(Route.ENTER_EMAIL)
          }}
        />
      ),
    }),
    [designLanguageStyles.errorLinkButton, dispatch, push]
  )

  const errorMessageComponentsRedirectToEmailLogin = useMemo(
    () => ({
      login: (
        <LinkButton
          key='sign-up-mobile-error-message-link'
          className={designLanguageStyles.errorLinkButton}
          onClick={() => {
            if (!isUndefinedOrEmpty(crossRegionUrl)) {
              xbtBrowserRedirect(crossRegionUrl)
            } else {
              dispatch(setIsMobileLogin(false))
              push(Route.ENTER_EMAIL)
            }
          }}
          data-testid='sign-up-mobile-error-login-link'
        />
      ),
    }),
    [crossRegionUrl, designLanguageStyles.errorLinkButton, dispatch, push, xbtBrowserRedirect]
  )

  const errorMessageComponentsRedirectToCashLogin = useMemo(
    () => ({
      login: (
        <LinkButton
          key={cashDuplicateErrorKey}
          className={designLanguageStyles.errorLinkButton}
          onClick={() => {
            if (!isUndefinedOrEmpty(crossRegionUrl) && !isDuplicatePhoneNumberError) {
              xbtBrowserRedirect(crossRegionUrl)
            } else {
              if (!isDuplicatePhoneNumberError) {
                dispatch(setIsMobileLogin(false))
              }
              if (isDuplicatePhoneNumberError) {
                dispatch(updateUseExistingMobile(true))
              }

              push(cashAuthRoute)
            }
          }}
          data-testid={cashDuplicateErrorKey}
        />
      ),
    }),
    [
      cashDuplicateErrorKey,
      crossRegionUrl,
      designLanguageStyles.errorLinkButton,
      dispatch,
      isDuplicatePhoneNumberError,
      push,
      xbtBrowserRedirect,
      cashAuthRoute,
    ]
  )

  useEffect(() => {
    if (isDuplicatePhoneNumberError) {
      logEvent(TrackingEvent.VIEWED_EXISTING_MOBILE_NUMBER_ERROR)
    }
  }, [isDuplicatePhoneNumberError])

  const { schema, initialFormData } = useMemo(() => {
    const components = {
      profileCard: () => <CreateAccountProfileCard enableEditName={enableEditName} setEnableEditName={setEnableEditName} />,
      signUpSubText: () => <Text size='S'>{signUpSubtext}</Text>,
      duplicateMobileError: () => (
        <Trans
          i18nKey={isCashRegistrationError ? 'signUp:error.existingPhoneNumberCashAccount' : 'signUp:error.existingPhoneNumber'}
          components={isCashRegistrationError ? errorMessageComponentsRedirectToCashLogin : errorMessageComponentsRedirectToMobileLogin}
        />
      ),
      marketingOptIn: () => {
        return (
          <Text color='Gray40' size='XXS'>
            {t('signUp:marketingOptInCheckboxLabel')}
          </Text>
        )
      },
      terms: () => <Disclaimer />,
      continueButton: () => {
        return (
          <Button.Primary loading={isSubmitted} size={buttonSize} type='submit' testNameSpace='sign-up-submit' disabled={isSubmitted} padding='Fluid'>
            {t('common:continue')}
          </Button.Primary>
        )
      },
      emailInputError: () => {
        switch (emailInputError) {
          case EmailInputError.DuplicatedCashEmailError:
            return <Trans i18nKey={`signUp:error.email.existingEmailCashAccount`} components={errorMessageComponentsRedirectToCashLogin} />
          case EmailInputError.DuplicatedEmailError:
            return <Trans i18nKey={`signUp:error.email.existingEmail`} components={errorMessageComponentsRedirectToEmailLogin} />
          case EmailInputError.EmptyEmailError:
            return t('signUp:error.email.emptyEmail')
          case EmailInputError.InvalidEmailError:
            return t('signUp:error.email.invalidEmail')
          case EmailInputError.UnsupportedMerchantTradingCountry:
            return t('signUp:error.email.unsupportedMerchantTradingCountry')
          case EmailInputError.UnknownEmailError:
            return t('signUp:error.email.unknownError')
          default:
            return t('signUp:error.email.unknownError')
        }
      },
      inlineError: () => {
        const isDuplicateIdentity = registrationError?.error === RegistrationErrorCode.DuplicateIdentity

        return (
          <Text color='Fire' size='XS' testNameSpace='sign-up-error'>
            <Trans
              i18nKey={`signUp:error.${registrationError?.error as string}`}
              components={isDuplicateIdentity ? errorMessageComponentsRedirectToEmailLogin : undefined}
            />
          </Text>
        )
      },
      handleCountryChange: (country) => {
        void handleCountryChange(country)
      },
      renderMobileTerms: () =>
        [Country.NZ, Country.US].includes(country) ? (
          <Grid>
            <Text color='Gray40' size='XXS' fontFace='CashSansMono' testNameSpace='mobile-terms-disclaimer'>
              {t('common:mobileTermsDisclaimer')}
            </Text>
          </Grid>
        ) : (
          <MobileTerms />
        ),
    }
    const handleGeocodeResult = (success: boolean): void => {
      setIsAddressPrefilled(success)
    }
    return getSchema(
      registrationData,
      components,
      t,
      givenNames,
      surname,
      enableEditName,
      consumer,
      locale,
      country,
      dobValidations,
      defaultMobile,
      isMobileLogin,
      isExpress,
      shouldVerify,
      parseMobile,
      supportedCountries,
      dispatch,
      isManualAddress,
      initialAddresses,
      logEvent,
      registrationError,
      registrationStatus,
      emailInputError,
      handleGeocodeResult,
      isArcadeDesignLanguage
    )
  }, [
    registrationData,
    t,
    givenNames,
    surname,
    enableEditName,
    consumer,
    country,
    dobValidations,
    defaultMobile,
    isMobileLogin,
    isExpress,
    shouldVerify,
    supportedCountries,
    isManualAddress,
    initialAddresses,
    buttonSize,
    registrationError,
    registrationStatus,
    emailInputError,
    isCashRegistrationError,
    signUpSubtext,
    isSubmitted,
    errorMessageComponentsRedirectToCashLogin,
    errorMessageComponentsRedirectToEmailLogin,
    errorMessageComponentsRedirectToMobileLogin,
    setEnableEditName,
    isArcadeDesignLanguage,
  ])

  const [formData, setFormData] = useState(initialFormData)
  useEffect(() => {
    formDataRef.current = formData
  }, [formData])
  const allInitialInputNames = useMemo(() => ['address', ...Object.keys(initialFormData)], [initialFormData])
  const preFilledInputNames = useRef(isValidObject(initialFormData) ? Object.keys(initialFormData).filter((key) => initialFormData[key]?.length > 0) : [])
  const creditCheckAgree = (registrationData?.countryCode ?? country) !== Country.CA

  const getPrefillKey = useCallback((input: string): string => {
    return camelCase(`prefill ${input}`)
  }, [])

  const getPrefilledAmplitudeObject = useCallback(
    (allInitialInputNames: string[], prefilledInputNames: string[]): PrefilledAmplitudePayload => {
      return Object.fromEntries(
        allInitialInputNames.map((inputName) => {
          const fieldString = FieldAnalyticsLabels[inputName]
          return [getPrefillKey(fieldString), prefilledInputNames.includes(inputName)]
        })
      )
    },
    [getPrefillKey]
  )
  const emailLookup = async (): Promise<boolean | undefined> => {
    if (!isMobileLogin) {
      return true
    }
    try {
      const res = await dispatch(handleEmailLookup(formData.email, true))
      const hasShadowAccount = 'shadowAccountExists' in res && valueIsNotNullOrUndefined(res?.shadowAccountExists) && res?.shadowAccountExists
      if ('error' in res && res.error === TerminalErrorCode.UnsupportedMerchantTradingCountry) {
        logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: ErrorCode.UnsupportedMerchantTradingCountry })
        setEmailInputError(EmailInputError.UnsupportedMerchantTradingCountry)

        return false
      } else if ('error' in res) {
        dispatch(setCheckoutError(res))
        setEmailInputError(EmailInputError.UnknownEmailError)

        return false
      } else if (isArcadeDesignLanguage && hasShadowAccount) {
        setEmailInputError(EmailInputError.DuplicatedCashEmailError)

        return false
      } else if ('existing' in res && res.existing) {
        setEmailInputError(EmailInputError.DuplicatedEmailError)

        return false
      } else if ('crossRegionCheckoutUrl' in res) {
        setCrossRegionUrl(res.crossRegionCheckoutUrl)
        setEmailInputError(EmailInputError.DuplicatedEmailError)
        return false
      } else {
        unwrapResult(await dispatch(createRegistration({ flow: VerificationMethod.SMS, locale })))
        return true
      }
    } catch {
      setEmailInputError(EmailInputError.UnknownEmailError)
    }
  }

  const softIdVerificationErrorHandling = async (errorCode: string): Promise<void> => {
    const errorCheckout: CheckoutError = getCheckoutIdentityError(errorCode)
    await dispatch(setCheckoutError(errorCheckout))
    push(Route.TERMINAL_ERROR)
  }

  const doSignup = async (data: CreateAccountForm): Promise<void> => {
    const request: CreateAccountRequest = getRequest(data, initialFormData, locale, creditCheckAgree)
    try {
      const emailLookupResult = await emailLookup()
      if (emailLookupResult !== true) {
        setIsSubmitted(false)
        return
      }
      if (shouldVerify) {
        await dispatch(updateRegistrationData(request))
        await dispatch(updateConsumerMobile(formData.mobile))
        push(Route.VERIFY)
      } else {
        await dispatch(updateRegistrationData(request))
        await dispatch(updateConsumerMobile(formData.mobile))

        unwrapResult(await dispatch(finaliseAccountDetails()))
        postCheckoutFlowMessage(CHECKOUT_FLOW_TYPE.ON_SIGNUP_COMPLETE)

        setDataLayer(GTM_EVENTS_NAME.ON_SIGNUP_COMPLETE)

        const account = unwrapResult(await dispatch(accountLookup()))
        push(postLoginRoute, account.preferredLocale)
      }
    } catch (error) {
      handleAccountDetailsErrors(error, softIdVerificationErrorHandling)
    } finally {
      setIsSubmitted(false)
    }
  }

  const handleOnChange = (data: CreateAccountForm): void => {
    CreateAccountAnalyticsFields.forEach((key) => {
      const fieldKey = key as keyof CreateAccountForm
      const analyticsFieldKey = fieldKey as keyof typeof hasSentAnalyticsData
      const newValue = data[fieldKey]
      const currentValue = formData[fieldKey]
      const hasLoggedAnalytics = hasSentAnalyticsData[analyticsFieldKey]

      if (!(isMobileLogin && fieldKey === 'email')) {
        if (valueIsNotNullOrUndefined(newValue) && valueIsNotNullOrUndefined(currentValue) && newValue !== currentValue && !hasLoggedAnalytics) {
          const wasPrefilled = preFilledInputNames.current.includes(key)
          logEvent(getEnteredPrefillEvent(FieldAnalyticsLabels[key]), { wasPrefilled: !!wasPrefilled })
          setHasSentAnalyticsData({ ...hasSentAnalyticsData, [analyticsFieldKey]: true })
        }
      }
    })

    if (valueIsNotNullOrUndefined(data.marketingOptIn) && data.marketingOptIn !== formData.marketingOptIn) {
      logEvent(TrackingEvent.TOGGLED_MARKETING_OPT_IN, { toggledValue: data.marketingOptIn })
    }

    if (isAddressPrefilled !== null) {
      if (!compareObjects(data.address, formData.address, false, [], true) && !hasSentAnalyticsData.address) {
        const wasPrefilled = preFilledInputNames.current.includes('address')
        logEvent(getEnteredPrefillEvent(FieldAnalyticsLabels.address), { wasPrefilled: !!wasPrefilled })
        setHasSentAnalyticsData({ ...hasSentAnalyticsData, address: true })
      }
    }

    if (formData.email !== data.email && emailInputError !== undefined) {
      setEmailInputError(undefined)
    }

    const { address } = data
    const dobValidations = getDOBValidations(t, address, country)
    setDobValidations(dobValidations)

    setFormData(data)
  }

  const handleOnSubmit = async (data: CreateAccountForm): Promise<void> => {
    logEvent(TrackingEvent.PRESSED_SIGN_UP_PAGE_CONTINUE)
    setIsSubmitted(true)
    const request: CreateAccountRequest = getRequest(data, initialFormData, locale, creditCheckAgree)
    dispatch(updateRegistrationData(request))
    await doSignup(data)
  }

  const handleStateChange = (isValid: boolean, errors: FormErrors): void => {
    if (!isValid) {
      setIsSubmitted(false)

      if (Boolean(errors?.givenNames) || Boolean(errors?.otherGivenNames) || Boolean(errors?.surname)) {
        const { VIEWED_NON_TERMINAL_ERROR, NAME_ERROR } = TrackingEvent
        handleInputErrorAnalytics(VIEWED_NON_TERMINAL_ERROR, { nonTerminalError: NAME_ERROR })
      }

      if (errors?.mobile?.type === 'invalid') {
        handleInputErrorAnalytics(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, {
          nonTerminalError: TrackingEvent.MOBILE_NUMBER_ERROR,
          mobileNumberLength: errors.mobile.ref?.value?.length ?? 0,
          countryCode: country,
        })
      }

      const dobErrorType = errors?.dateOfBirth?.type
      if (dobErrorType === 'underAge' || dobErrorType === 'overAge') {
        const validity = dobErrorType === 'underAge' ? InputBirthdayValidity.UnderAge : InputBirthdayValidity.OverAge
        const { VIEWED_NON_TERMINAL_ERROR } = TrackingEvent
        handleInputErrorAnalytics(VIEWED_NON_TERMINAL_ERROR, { nonTerminalError: DOB_ERROR_MAP[validity] })
      }

      if (errors?.address?.type === 'requiredAddressFields') {
        handleInputErrorAnalytics(TrackingEvent.ADDRESS_ERROR)
      }
    }
  }

  useEffect(() => {
    postCheckoutFlowMessage(CHECKOUT_FLOW_TYPE.ON_SIGNUP_START)
  }, [])

  useEffect(() => {
    if (isAddressPrefilled !== null && !preFilledInputNames.current.includes('address')) {
      preFilledInputNames.current = [...preFilledInputNames.current, ...(isAddressPrefilled ? ['address'] : [])]
    }
  }, [isAddressPrefilled])

  useEffect(() => {
    const { notLoggedPrefillStatus } = oneOffEvents

    if (isAddressPrefilled === null) {
      return
    }

    if (preFilledInputNames.current.length > 0 && notLoggedPrefillStatus) {
      const ampPayload = getPrefilledAmplitudeObject(allInitialInputNames, preFilledInputNames.current)

      logEvent(TrackingEvent.TRIGGER_PREFILL_STATUS, ampPayload)

      setOneOffEvents((events) => ({ ...events, notLoggedPrefillStatus: false }))
    }
  }, [preFilledInputNames, oneOffEvents, logEvent, isAddressPrefilled, allInitialInputNames, getPrefilledAmplitudeObject])

  return (
    <>
      <FormWrapper>
        <FormBuilder
          schema={schema}
          testNameSpace={'sign-up'}
          onSubmit={(data) => {
            void handleOnSubmit(data)
          }}
          onChange={handleOnChange}
          onStateChange={handleStateChange}
        />
      </FormWrapper>
      {isSubmitted && <DisabledModal />}
    </>
  )
}
